import { ManualPart } from '../types';

export const part7: ManualPart = {
  id: "part-7",
  title: "GLOSARIO TÉCNICO Y FÓRMULAS",
  sections: [
    {
      id: "glossary-full-v3",
      title: "GLOSARIO COMPLETO DE LIVESYNC PRO",
      content: [
        "**🌡️ ENTORNO Y ATMÓSFERA**",
        "• **Temperatura (°C):** Temperatura del aire ambiente. Afecta la velocidad del sonido (343 m/s a 20°C). Cada 1°C de aumento acelera el sonido ~0.6 m/s. Crítico para cálculos de delay y refracción.",
        "• **Temperatura Showtime (°C):** Temperatura esperada durante el evento. Si difiere de la temperatura de setup, LiveSync calcula refracción térmica (curvatura del sonido). El aire caliente encima del frío curva el sonido HACIA ARRIBA. El aire frío encima del caliente curva el sonido HACIA ABAJO.",
        "• **Humedad Relativa (%):** Porcentaje de humedad del aire (0-100%). La humedad ABSORBE alta frecuencia. En espacios secos (< 30%), los agudos 'viajan más lejos'. En espacios húmedos (> 70%), se atenúan rápidamente.",
        "• **Presión Atmosférica (kPa):** Se calcula automáticamente según altitud. Afecta la absorción atmosférica. A mayor altitud (menor presión), hay MÁS absorción de HF.",
        "• **Altitud (m):** Metros sobre el nivel del mar. Reduce la presión atmosférica. LiveSync ajusta automáticamente la velocidad del sonido y absorción.",
        "• **Velocidad del Viento (m/s):** Afecta la propagación: 0-3 m/s (sin impacto), 5.5-10 m/s (refracción significativa), >10 m/s (PELIGRO ESTRUCTURAL), >15 m/s (CRÍTICO - BAJAR PA).",
        "• **Dirección del Viento:** 0° (Viento de frente), 180° (Viento de cola - PELIGROSO), 90°/270° (Viento cruzado), Tailwind (hacia abajo), Headwind (hacia arriba).",
        "• **Tipo de Suelo (Ground Type):** grass (absorbe graves), concrete (altamente reflectivo), sand (muy absorbente), asfalto, crowd (multitud de pie).",
        "• **Ground Effect:** Comb filtering causado por la interferencia entre el sonido directo y el reflejado en el suelo. Crea notches (caídas) en frecuencias específicas.",
        "",
        "**👥 CONTROL DE OCUPACIÓN Y PÚBLICO**",
        "• **Expected Attendees:** Número estimado de personas. Densidades realistas: Outdoor standing (2.2 p/m²), Indoor standing (2.0 p/m²), Theater seating (1.2 p/m²), Corporate tables (0.9 p/m²).",
        "• **Venue Occupancy (%):** Porcentaje de llenura. Afecta temperatura (+0.05°C por 1%), humedad (+0.1% por 1%) y absorción de HF (~0.5 dB/100m adicional).",
        "",
        "**🏛️ ACÚSTICA DE SALA (RT60)**",
        "• **RT60 (Reverberation Time):** Tiempo en segundos que tarda el sonido en decaer 60 dB. Ideal: 0.3-0.5s. Catedral / Ininteligible: > 3.0s.",
        "• **Fórmulas Sabine/Eyring:** Cálculos teóricos de RT60 basados en volumen y absorción promedio.",
        "• **Field Measured RT60:** Valor REAL medido con SMAART, REW o método del globo (ISO 3382).",
        "• **Room Modes (Modos de Sala):** Frecuencias de resonancia natural determinadas solo por geometría (L, W, H). Tipos: Axial (más fuertes), Tangential y Oblique.",
        "• **Critical Distance (Dc):** Distancia donde sonido directo = sonido reverberante. Si el público está > Dc → NECESITAS DELAY TOWERS.",
        "• **STI (Speech Transmission Index):** Métrica de inteligibilidad (0 a 1). >0.6 Buena; >0.75 Excelente claridad.",
        "• **Image Source Reflections:** Cálculo de reflexiones tempranas (<80ms). Las de <20ms se integran constructivamente.",
        "",
        "**🎚️ ROOM EQ CORRECTION (CORRECCIÓN DE SALA)**",
        "• **Room EQ:** Curva correctiva generada automáticamente usando SOLO CUTS para preservar el headroom.",
        "• **Tipos de Filtro:** HPF (pasa-altos), Low Shelf, High Shelf, Bell (Peaking), Notch (quirúrgico).",
        "• **Q Factor:** Ancho del filtro. Valores altos = más estrecho (ej. Q=10 para notch quirúrgico).",
        "",
        "**🔊 SISTEMA PRINCIPAL (PA)**",
        "• **LF Coupling Gain:** Ganancia por acoplamiento en graves: 20 × log10(N). 8 cajas = +18 dB.",
        "• **Power Alley:** Callejón de sumación constructiva (+6dB) en el eje central de un sistema estéreo.",
        "• **Array Length:** Longitud física total. Determina directividad vertical en HF y transición Near/Far Field.",
        "",
        "**🎚️ EDITOR VISUAL DE SPLAY**",
        "• **Splay Angles:** Ángulos entre cajas. J-Curve (gradual), Straight (iguales), Arc (curvatura audiencia).",
        "• **Broken Line Source:** Ocurre cuando un splay excede la dispersión vertical nominal (causa gaps en cobertura).",
        "• **WST Criteria:** Reglas para mantener coherencia en HF: d × sin(θ) < λ/2.",
        "",
        "**🔊 SUBWOOFER ARRAY DESIGNER**",
        "• **Omni:** Radiación 360°. Más SPL frontal pero mucho 'spill' hacia el escenario.",
        "• **Cardioid (CSA):** Configuración 2:1 para rechazar ~15dB hacia atrás mediante desfase físico λ/4.",
        "• **End-Fire:** Subs en línea con delay progresivo para máximo tiro y rechazo trasero.",
        "",
        "**🎯 OBJETIVOS DE CALIBRACIÓN**",
        "• **Target SPL:** Nivel de presión sonora deseado (Promedio temporal LEQ).",
        "• **Target Distance:** Distancia horizontal desde el PA hasta el oyente objetivo.",
        "• **FOH Distance:** Distancia óptima aproximada de 1.5 × Ancho del Escenario.",
        "",
        "**⚡ AMPLIFICACIÓN Y ELECTRICIDAD**",
        "• **Topologías:** Modern PFC (PF=0.95, eficiencia 90%), Modern D (eficiencia 85%), Legacy AB (antiguos).",
        "• **Damping Factor:** Relación de impedancia speaker/amp. >100 óptimo; <50 graves flojos.",
        "• **Neutral Current:** Corriente de retorno en trifásico. En sistemas balanceados es casi cero.",
        "• **Cable Gauge (AWG):** 12 AWG (estándar), 8 AWG (subs/largo alcance).",
        "• **Voltage Drop:** Pérdida en cables. Si > 5%: pérdida de potencia y graves débiles.",
        "",
        "**🌐 RED Y AUDIO DIGITAL**",
        "• **Protocolos:** Dante (Dante Audinate), AVB (IEEE 1722).",
        "• **Ancho de Banda:** LiveSync calcula Mbps basado en Sample Rate, Bit Depth y canales.",
        "",
        "**🏗️ RIGGING**",
        "• **SWL:** Capacidad de trabajo segura por punto.",
        "• **Safety Margin:** (Carga / Capacidad) × 100. < 75% seguro; > 90% CRÍTICO.",
        "• **Bridle:** Sistema de distribución de peso con piernas y pickup point.",
        "",
        "**📏 DELAY TOWERS**",
        "• **Required Delay:** (Slant_main - Slant_tower) / c × 1000.",
        "• **Haas Effect:** Fusión psicoacústica (<30ms) que localiza la fuente en el primer arribo.",
        "",
        "**📊 ACOUSTIC ANALYSIS**",
        "• **Distance Loss:** -6dB por cada doble de distancia (Ley del inverso cuadrado).",
        "• **Atmospheric Loss:** Absorción del aire dependiente de la frecuencia (ISO 9613-1).",
        "• **Headroom:** Margen entre SPL_max y SPL_requerido (Óptimo > 12dB).",
        "• **Crest Factor:** Diferencia entre peak y RMS (típicamente 18dB para audio)."
      ]
    },
    {
      id: "formula-dictionary-exhaustive-v2",
      title: "DICCIONARIO DE FÓRMULAS",
      content: [
        "**FÍSICA ATMOSFÉRICA**",
        "• **Velocidad del Sonido (c):** `c = 331.3 + (0.606 × T)`",
        "• **Pérdida por Distancia (dB):** `Loss = 20 × log10(distance)`",
        "• **Delay Requerido (ms):** `Delay = (Distancia_Slant / c) × 1000`",
        "",
        "**ACÚSTICA DE SALA**",
        "• **RT60 (Sabine):** `RT60 = 0.161 × V / (S × α)`",
        "• **RT60 (Eyring):** `RT60 = 0.161 × V / (-S × ln(1 - α))`",
        "• **Distancia Crítica (Dc):** `Dc = 0.057 × √(Q × V / RT60)`",
        "• **Frecuencia Modo (Hz):** `f = (c/2) × √((nx/L)² + (ny/W)² + (nz/H)²)`",
        "",
        "**INGENIERÍA DE SISTEMAS**",
        "• **LF Coupling Gain (dB):** `Gain = 20 × log10(N_boxes)`",
        "• **Espaciado Sub λ/4 (m):** `Spacing = c / (4 × f_objetivo)`",
        "• **Ancho de Banda Red (Mbps):** `Mbps = (SampleRate × BitDepth × Canales / 1M) × ProtocolOverhead`",
        "• **Transición Line Array (m):** `Transition = L² × f / (2 × c)`",
        "",
        "**ELÉCTRICA Y LOGÍSTICA**",
        "• **Damping Factor (DF):** `DF = Z_speaker / (Z_amp_output + R_cable)`",
        "• **Caída Voltaje (%):** `V_drop% = (R_cable / (R_cable + Z_speaker)) × 100`",
        "• **Carga por Punto (kg):** `Load = (ArrayWeight + Gear) × 1.25 / Points`",
        "• **Generador (kVA):** `kVA = (TotalWatts / 1000) × 1.25 / PowerFactor`"
      ]
    }
  ]
};